import { useState, useEffect } from 'react';
import { useOnChange, usePageParam } from '../../hooks/usePageParam';
import { useQuery } from '@apollo/client';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { LOGS_QUERY } from '../../graphql/queries';
import styles from './ActivityLogs.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

const ACTION_CONFIG: Record<string, { label: string; icon: IconName; color: string }> = {
  user_login: { label: 'Login', icon: 'login', color: 'bg-blue-50 text-blue-600' },
  user_logout: { label: 'Logout', icon: 'logout', color: 'bg-gray-100 text-gray-600' },
  user_register: { label: 'Registered', icon: 'person_add', color: 'bg-green-50 text-green-600' },
  project_created: { label: 'Project Created', icon: 'create_new_folder', color: 'bg-primary/10 text-primary' },
  project_updated: { label: 'Project Updated', icon: 'edit', color: 'bg-blue-50 text-blue-600' },
  project_deleted: { label: 'Project Deleted', icon: 'delete', color: 'bg-red-50 text-error' },
  project_archived: { label: 'Project Archived', icon: 'archive', color: 'bg-gray-100 text-gray-600' },
  file_uploaded: { label: 'File Uploaded', icon: 'cloud_upload', color: 'bg-green-50 text-green-600' },
  file_deleted: { label: 'File Deleted', icon: 'delete', color: 'bg-red-50 text-error' },
  file_viewed: { label: 'File Viewed', icon: 'visibility', color: 'bg-blue-50 text-blue-600' },
  file_downloaded: { label: 'File Downloaded', icon: 'download', color: 'bg-green-50 text-green-600' },
  member_invited: { label: 'Member Invited', icon: 'person_add', color: 'bg-purple-50 text-purple-600' },
  member_removed: { label: 'Member Removed', icon: 'person_remove', color: 'bg-red-50 text-error' },
  member_role_changed: { label: 'Role Changed', icon: 'manage_accounts', color: 'bg-orange-50 text-orange-600' },
  share_link_created: { label: 'Link Created', icon: 'add_link', color: 'bg-primary/10 text-primary' },
  share_link_revoked: { label: 'Link Revoked', icon: 'link_off', color: 'bg-red-50 text-error' },
  share_link_viewed: { label: 'Link Viewed', icon: 'visibility', color: 'bg-blue-50 text-blue-600' },
  project_transfer_requested: { label: 'Transfer Requested', icon: 'swap_horiz', color: 'bg-orange-50 text-orange-600' },
  project_transfer_accepted: { label: 'Transfer Accepted', icon: 'swap_horiz', color: 'bg-green-50 text-green-600' },
  project_transfer_cancelled: { label: 'Transfer Cancelled', icon: 'swap_horiz', color: 'bg-gray-100 text-gray-600' },
};

const ACTION_GROUPS: { label: string; actions: string[] }[] = [
  { label: 'All', actions: [] },
  {
    label: 'Projects',
    actions: [
      'project_created', 'project_updated', 'project_deleted', 'project_archived',
      'project_transfer_requested', 'project_transfer_accepted', 'project_transfer_cancelled',
    ],
  },
  { label: 'Files', actions: ['file_uploaded', 'file_deleted', 'file_viewed', 'file_downloaded'] },
  { label: 'Members', actions: ['member_invited', 'member_removed', 'member_role_changed'] },
  { label: 'Sharing', actions: ['share_link_created', 'share_link_revoked', 'share_link_viewed'] },
  { label: 'Auth', actions: ['user_login', 'user_logout', 'user_register'] },
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ActivityLogs = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userIdFilter = searchParams.get('userId') || undefined;
  const targetUserName = (location.state as { userName?: string } | null)?.userName;

  const [search, setSearch] = useState('');
  const [groupLabel, setGroupLabel] = useState('All');
  const [page, setPage] = usePageParam();
  const limit = 30;

  useOnChange(() => setPage(1), [userIdFilter]);

  const groupActions = ACTION_GROUPS.find((g) => g.label === groupLabel)?.actions ?? [];

  const { data, loading, error } = useQuery(LOGS_QUERY, {
    variables: {
      search: search || undefined,
      actions: groupActions.length ? groupActions : undefined,
      userId: userIdFilter,
      page,
      limit,
    },
    fetchPolicy: 'cache-and-network',
  });

  const logs = data?.logs?.logs || [];
  const total = data?.logs?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  // A ?page= past the end (an old link, rows gone since) moves to the last page.
  useEffect(() => {
    if (data && page > Math.max(1, totalPages)) setPage(Math.max(1, totalPages));
  }, [data, page, totalPages, setPage]);

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <DashboardLayout onSearch={changeSearch} searchPlaceholder="Search logs...">
      <div className="space-y-6">
        <div className={`flex justify-between items-end ${styles.pageHeader}`}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Activity Logs</h2>
            <p className="text-on-surface-variant mt-1">Complete audit trail of all account and project activities.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-on-surface-variant">{total} entries</span>
          </div>
        </div>

        {userIdFilter && (
          <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <Icon name="filter_alt" className="text-blue-600" />
              <p className="text-sm text-blue-900 truncate">
                Showing activity for{' '}
                <strong>{targetUserName || `user ${userIdFilter.slice(-6)}`}</strong>
              </p>
            </div>
            <button
              onClick={() => navigate('/activity')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1 flex-shrink-0"
            >
              <Icon name="close" className="text-sm" />
              View all logs
            </button>
          </div>
        )}

        <div className={`flex items-center gap-2 bg-surface-container-low rounded-xl p-1.5 w-fit ${styles.filterBar}`}>
          {ACTION_GROUPS.map((g) => (
            <button
              key={g.label}
              onClick={() => { setGroupLabel(g.label); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                groupLabel === g.label
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">
              <Icon name="refresh" className="animate-spin mr-2" />
              Loading logs...
            </div>
          ) : error ? (
            <div role="alert" className="m-4 p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
              <Icon name="error" className="text-base" />
              Could not load the activity log: {error.message}
            </div>
          ) : logs.length === 0 ? (
            <div className="py-16 text-center">
              <Icon name="receipt_long" className="text-4xl text-outline mb-3 block" />
              <p className="text-on-surface-variant">No activity logs found</p>
            </div>
          ) : (
            <>
              {logs.map((log: { id: string; action: string; description: string; createdAt: string; user?: { id: string; firstName: string; lastName: string; avatar?: string }; project?: { id: string; name: string }; file?: { id: string; originalName: string }; ipAddress?: string }) => {
                const config = ACTION_CONFIG[log.action] || { label: log.action, icon: 'info', color: 'bg-gray-100 text-gray-600' };
                return (
                  <div key={log.id} className={styles.logRow}>
                    <div className={`${styles.actionIcon} ${config.color}`}>
                      <Icon name={config.icon} className="text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${config.color}`}>{config.label}</span>
                        {log.project && (
                          <span className="text-[10px] text-on-surface-variant">
                            in <span className="font-medium text-on-surface">{log.project.name}</span>
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-on-surface mt-0.5">{log.description}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {log.user && (
                          <span className="text-xs text-on-surface-variant flex items-center gap-1">
                            <Icon name="person" className="text-sm" />
                            {log.user.firstName} {log.user.lastName}
                          </span>
                        )}
                        {log.ipAddress && (
                          <span className="text-xs text-on-surface-variant flex items-center gap-1">
                            <Icon name="wifi" className="text-sm" />
                            {log.ipAddress}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-on-surface-variant whitespace-nowrap flex-shrink-0">
                      {formatDate(log.createdAt)}
                    </div>
                  </div>
                );
              })}

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-outline-variant/10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-1 text-sm font-medium text-on-surface-variant hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Icon name="chevron_left" className="text-lg" />
                    Previous
                  </button>
                  <span className="text-sm text-on-surface-variant">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="flex items-center gap-1 text-sm font-medium text-on-surface-variant hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <Icon name="chevron_right" className="text-lg" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ActivityLogs;
